import {
  HIDE_MONEY_BASE_KEY,
  HOLDING_STORE_BASE_KEY,
  HUB_REFRESH_BASE_KEY,
  scopedAssetKey
} from './assetStorageScope';

/** 云端/备份包内规范键（不带用户后缀） */
const STORE_KEY = HOLDING_STORE_BASE_KEY;
const HIDE_MONEY_KEY = HIDE_MONEY_BASE_KEY;
const MP_STORE_KEY = 'holding_store_v1';
const MP_HIDE_MONEY_KEY = 'hide_money_v1';
const MP_BACKUP_SCHEMA = 'wx-miniapp-storage-backup';

export const BACKUP_SCHEMA = 'web-asset-center-storage-backup';
export const BACKUP_VERSION = 1;
export { MP_BACKUP_SCHEMA };

const DERIVED_CACHE_PREFIXES = [
  'asset_profit_day_cache_v1_',
  'asset_trend_nav_cache_v1_'
];

/** 恢复/导入备份前清空衍生缓存，避免旧日收益与新区间叠加 */
export function clearAllAssetDerivedCaches() {
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (DERIVED_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        window.localStorage.removeItem(key);
      }
      // hub refresh：全局旧键 + 各用户分桶
      if (key === HUB_REFRESH_BASE_KEY || key.startsWith(`${HUB_REFRESH_BASE_KEY}_u_`) || key === `${HUB_REFRESH_BASE_KEY}__logged_out`) {
        window.localStorage.removeItem(key);
      }
    }
    try {
      window.localStorage.removeItem(scopedAssetKey(HUB_REFRESH_BASE_KEY));
    } catch (_) {
      // ignore
    }
  } catch (error) {
    // ignore
  }
}

function parseStoredHoldings(raw) {
  if (raw == null) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || !Array.isArray(parsed.holdings)) return null;
    return parsed.holdings;
  } catch (error) {
    return null;
  }
}

export function extractHoldingsFromBackupPayload(payload) {
  const data = payload?.data || {};
  return (
    parseStoredHoldings(data[STORE_KEY])
    || parseStoredHoldings(data[MP_STORE_KEY])
    || null
  );
}

export function countHoldingsInBackupPayload(payload) {
  const holdings = extractHoldingsFromBackupPayload(payload);
  return holdings ? holdings.length : 0;
}

export function normalizeBackupPayloadForWeb(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const data = { ...(payload.data || {}) };

  if (data[MP_STORE_KEY] != null && data[STORE_KEY] == null) {
    data[STORE_KEY] = typeof data[MP_STORE_KEY] === 'string'
      ? data[MP_STORE_KEY]
      : JSON.stringify(data[MP_STORE_KEY]);
  }
  if (data[MP_HIDE_MONEY_KEY] != null && data[HIDE_MONEY_KEY] == null) {
    const hideValue = data[MP_HIDE_MONEY_KEY];
    data[HIDE_MONEY_KEY] = hideValue === true || hideValue === '1' || hideValue === 1 ? '1' : '0';
  }

  return {
    ...payload,
    schema: payload.schema === MP_BACKUP_SCHEMA ? BACKUP_SCHEMA : (payload.schema || BACKUP_SCHEMA),
    data
  };
}

function pickLocal(canonicalKey) {
  try {
    const v = window.localStorage.getItem(scopedAssetKey(canonicalKey));
    return v == null ? null : v;
  } catch (error) {
    return null;
  }
}

function setLocal(canonicalKey, value) {
  try {
    const key = scopedAssetKey(canonicalKey);
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
}

/** 打包为小程序 storage-backup 可读的对象（非 localStorage 字符串） */
function buildMiniProgramHoldingStore() {
  const raw = pickLocal(STORE_KEY);
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.holdings)) return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

function buildMiniProgramHideMoney() {
  const raw = pickLocal(HIDE_MONEY_KEY);
  return raw === '1' || raw === 1 || raw === true;
}

/**
 * 云端/跨端备份统一使用小程序 schema 与 key，便于小程序「从云端恢复」直接识别。
 * 网页恢复时经 normalizeBackupPayloadForWeb 转回本地 key。
 */
export function buildBackupPayload() {
  return {
    schema: MP_BACKUP_SCHEMA,
    version: BACKUP_VERSION,
    createdAt: Date.now(),
    data: {
      [MP_STORE_KEY]: buildMiniProgramHoldingStore(),
      [MP_HIDE_MONEY_KEY]: buildMiniProgramHideMoney()
    }
  };
}

/** 上传前自检：必须是小程序可识别的 schema */
export function assertMiniProgramBackupPayload(payload) {
  const schema = String(payload?.schema || '');
  if (schema !== MP_BACKUP_SCHEMA) {
    return {
      ok: false,
      message: '当前页面版本过旧，备份格式仍是旧版。请关闭所有 oldox.vip 标签页后重新打开，或清除浏览器网站数据，再执行云端备份。'
    };
  }
  const hs = payload?.data?.[MP_STORE_KEY];
  if (hs != null && (typeof hs !== 'object' || !Array.isArray(hs.holdings))) {
    return { ok: false, message: '持仓数据打包异常，请刷新页面后重试' };
  }
  return { ok: true };
}

export function stringifyBackup(payload, pretty = true) {
  return JSON.stringify(payload, null, pretty ? 2 : 0);
}

export function exportBackupToText(pretty = true) {
  return stringifyBackup(buildBackupPayload(), pretty);
}

export function parseBackup(text) {
  try {
    const payload = JSON.parse(String(text || ''));
    if (!payload || typeof payload !== 'object') {
      return { ok: false, message: '备份格式无效' };
    }
    return { ok: true, payload };
  } catch (error) {
    return { ok: false, message: '备份 JSON 解析失败' };
  }
}

export function validateBackupPayload(payload) {
  if (!payload || typeof payload !== 'object') return { ok: false, message: '备份为空' };
  const schema = String(payload.schema || '');
  if (schema && schema !== BACKUP_SCHEMA && schema !== MP_BACKUP_SCHEMA) {
    return { ok: false, message: '备份 schema 不匹配' };
  }
  const holdings = extractHoldingsFromBackupPayload(payload);
  if (holdings == null) {
    const data = payload.data || {};
    if (data[STORE_KEY] == null && data[MP_STORE_KEY] == null) return { ok: true };
    return { ok: false, message: '持仓数据结构不正确' };
  }
  return { ok: true };
}

export function applyBackupPayload(payload) {
  const normalized = normalizeBackupPayloadForWeb(payload);
  const valid = validateBackupPayload(normalized);
  if (!valid.ok) return valid;

  clearAllAssetDerivedCaches();

  const data = normalized.data || {};
  const webData = {
    [STORE_KEY]: data[STORE_KEY],
    [HIDE_MONEY_KEY]: data[HIDE_MONEY_KEY]
  };
  Object.entries(webData).forEach(([key, value]) => {
    if (value == null) setLocal(key, null);
    else setLocal(key, typeof value === 'string' ? value : JSON.stringify(value));
  });
  try {
    // 恢复后通知 UI；云同步侧若在 suppressNotify 中则不会标 dirty
    window.dispatchEvent(new CustomEvent('asset-holdings-changed'));
  } catch (_) {
    // ignore
  }
  return { ok: true, holdingCount: countHoldingsInBackupPayload(normalized) };
}
